import { Briefcase, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Loader } from "@/components/common/loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { deleteEmployer } from "@/features/clients/api";
import { EmployerFormDialog } from "@/features/clients/components/employer-form-dialog";
import { useEmployers } from "@/features/clients/hooks/use-client-records";
import type { Employer, PayPeriod } from "@/features/clients/types";

const PAY_PERIOD_LABELS: Record<Exclude<PayPeriod, "">, string> = {
  WEEKLY: "Weekly",
  BIWEEKLY: "Biweekly",
  SEMIMONTHLY: "Semimonthly",
  MONTHLY: "Monthly",
};

export function EmployersSection({ clientId }: { clientId: string }) {
  const { records: employers, loading, error, refetch } = useEmployers(clientId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Employer | null>(null);
  const [deleting, setDeleting] = useState<Employer | null>(null);

  function openAdd() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(employer: Employer) {
    setEditing(employer);
    setDialogOpen(true);
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await deleteEmployer(clientId, deleting.id);
      toast.success("Employer removed");
      refetch();
    } catch {
      toast.error("Couldn't remove employer.");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 flex items-center justify-between border-b px-6 py-4">
        <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
          <Briefcase className="text-primary size-5" /> Employers
        </h3>
        <Button type="button" size="sm" onClick={openAdd}>
          <Plus className="size-4" /> Add Employer
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : error ? (
        <p className="text-destructive px-6 py-8 text-sm">{error}</p>
      ) : employers.length === 0 ? (
        <p className="text-muted-foreground px-6 py-8 text-sm">
          No employers recorded for this client yet.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employer of</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Occupation</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Pay Period</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employers.map((emp) => (
              <TableRow key={emp.id}>
                <TableCell>
                  <Badge variant={emp.belongsTo === "SPOUSE" ? "secondary" : "outline"}>
                    {emp.belongsTo === "SPOUSE" ? "Spouse" : "Taxpayer"}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium">{emp.name}</TableCell>
                <TableCell>{emp.occupation || "—"}</TableCell>
                <TableCell>
                  {emp.city && emp.state ? `${emp.city}, ${emp.state}` : emp.city || emp.state || "—"}
                </TableCell>
                <TableCell>{emp.phone || "—"}</TableCell>
                <TableCell>{emp.payPeriod ? PAY_PERIOD_LABELS[emp.payPeriod] : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${emp.name}`}
                    onClick={() => openEdit(emp)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${emp.name}`}
                    onClick={() => setDeleting(emp)}
                  >
                    <Trash2 className="text-destructive size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <EmployerFormDialog
        clientId={clientId}
        employer={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={refetch}
      />

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove employer?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {deleting?.name} from this client's profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
